import styled from '@emotion/styled'
import { ContextKey } from '@pubstack/common/src/contextKey'
import { noSpaceQuoteAllowedRegExp } from '@pubstack/common/src/input'
import { ConditionGroup, StackContext } from '@pubstack/common/src/stackContext'
import { FunctionComponent, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { contextNameErrorMessage } from '~/api/adm/contexts.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { BaseInputElement, Input } from '~/components/Input'
import { PureAdmEditPage } from '~/modules/adm/PureAdmEditPage'
import { ContextConditionsForm } from './ContextConditionsForm'

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  max-width: 1000px;
  ${Fonts.P1}
  h2 {
    ${Fonts.H2}
    font-weight: 500;
  }

  & > ${BaseInputElement} {
    width: 292px;
    margin-bottom: 36px;
    padding: 20px 0;
  }
`

const Subtitle = styled.div`
  ${Fonts.P1}
  span {
    display: inline-flex;
    align-items: center;
  }
`

const FormButtonsWrapper = styled.div`
  text-align: right;
  padding: 20px;
  ${Button} {
    margin-left: 15px;
  }
`

type PureAdmContextEditPageProps = {
  isEditing: boolean
  context: StackContext
  contextKeys: ContextKey[]
  onBack: () => void
  breadcrumbs: React.ReactNode
  createContext: (context: StackContext) => Promise<void>
}

export const PureAdmContextEditPage: FunctionComponent<PureAdmContextEditPageProps> = ({ context, contextKeys, isEditing, onBack, createContext, breadcrumbs }) => {
  const defaultConditions: ConditionGroup = {
    ...context?.conditions,
    elements: context.conditions.elements.length ? context.conditions.elements : [{ op: 'IN', key: '', values: [] }],
  }
  const formMethods = useForm<StackContext>({ defaultValues: { ...context, conditions: defaultConditions }, mode: 'onChange' })
  const {
    handleSubmit,
    formState: { errors },
    reset,
    resetField,
    control,
    setError,
  } = formMethods

  useEffect(() => {
    reset({
      ...context,
      conditions: defaultConditions,
    })
  }, [context])

  /**
   * A name is only known to be free once the server has looked, and the form is where the author can do
   * something about it: the refusal goes back on the field they typed rather than into a toast that leaves the
   * page looking like it simply failed to save.
   */
  const onSubmit: SubmitHandler<StackContext> = async (newContext) => {
    try {
      await createContext(newContext)
    } catch (error) {
      setError('name', { message: contextNameErrorMessage(error) ?? 'This name cannot be used.' }, { shouldFocus: true })
    }
  }

  return (
    <PureAdmEditPage title={isEditing ? 'Edit context' : 'New context'} breadcrumbs={breadcrumbs} color={Colors.Violet} onBackClick={onBack}>
      <FormProvider {...formMethods}>
        <form id={'newContextForm'} onSubmit={handleSubmit(onSubmit)}>
          <FormWrapper>
            <Subtitle>
              <h2>Name</h2>
            </Subtitle>
            <Input
              type={'text'}
              label={'Name'}
              control={control}
              name={'name'}
              error={errors.name?.message}
              rules={{
                required: { value: true, message: 'Name required.' },
                pattern: { value: noSpaceQuoteAllowedRegExp, message: 'Quotes and spaces are not allowed.' },
              }}
              disabled={isEditing}
              helper={!isEditing && 'Cannot be edited later.'}
              maxLength={100}
            />
            <Subtitle>
              <h2>Conditions</h2>
            </Subtitle>
            <ContextConditionsForm contextKeys={contextKeys} onKeyChange={(index) => resetField(`conditions.elements.${index}.values`)} />
          </FormWrapper>
          <FormButtonsWrapper>
            <Button variant={'tertiary'} onClick={onBack} trackingLabel={'adm-contexts-cancel'}>
              Cancel
            </Button>
            {isEditing ? (
              <Button variant={'primary'} type={'submit'} trackingLabel={'adm-contexts-save'}>
                Update
              </Button>
            ) : (
              <Button variant={'primary'} type={'submit'} trackingLabel={'adm-contexts-save'}>
                Validate
              </Button>
            )}
          </FormButtonsWrapper>
        </form>
      </FormProvider>
    </PureAdmEditPage>
  )
}
